import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { getMonth, getYear, set } from 'date-fns'

import { EMPTY_OBJECT } from '../../../constants/common'
import { NUMBER_SIZE } from '../../../constants/size'
import { useTimeZone, useVariableRef } from '../../../hooks'
import { createDate } from '../../../utils/date'
import { MonthSelect } from '../MonthSelect'
import { MonthSpinInput } from '../MonthSpinInput'
import { YearSelect } from '../YearSelect'

import { getMaxMonth, getMinMonth } from './MonthInput.utils'

import * as styles from './MonthInput.module.css'

function MonthInput({
  Element = 'div',
  Month = MonthSelect,
  SpinInput = MonthSpinInput,
  Year = YearSelect,
  borderless,
  className = styles.MonthInput,
  disabled,
  maxValue,
  minValue,
  monthProps: {
    className: monthClassName = styles.month,
    disabled: monthDisabled,
    ...monthProps
  } = EMPTY_OBJECT,
  size = NUMBER_SIZE.size40,
  spinInputProps: {
    className: spinInputClassName = styles.spinInput,
    disabled: spinInputDisabled,
    ...spinInputProps
  } = EMPTY_OBJECT,
  value,
  yearProps: {
    className: yearClassName = styles.year,
    disabled: yearDisabled,
    ...yearProps
  } = EMPTY_OBJECT,
  onChange,
  ...elementProps
}) {
  const timeZone = useTimeZone()
  const timeZoneRef = useRef(timeZone)

  const onChangeRef = useVariableRef(onChange)
  const valueRef = useVariableRef(value)

  useEffect(() => {
    if (timeZone !== timeZoneRef.current) {
      const { current: currValue } = valueRef

      timeZoneRef.current = timeZone
      onChangeRef.current?.(
        createDate(timeZone, getYear(currValue), getMonth(currValue))
      )
    }
  }, [onChangeRef, timeZone, valueRef])

  function handleMonthChange(month) {
    onChange?.(set(value, { month }))
  }

  function handleYearChange(year) {
    onChange?.(set(value, { year }))
  }

  return (
    <Element
      {...elementProps}
      className={classNames(className, size ? styles[`size${size}`] : null)}
    >
      <Month
        {...monthProps}
        borderless={borderless}
        className={monthClassName}
        disabled={monthDisabled || disabled}
        maxValue={getMaxMonth(value, maxValue)}
        minValue={getMinMonth(value, minValue)}
        size={size}
        value={getMonth(value)}
        onChange={handleMonthChange}
      />
      <Year
        {...yearProps}
        borderless={borderless}
        className={yearClassName}
        disabled={yearDisabled || disabled}
        maxValue={maxValue == null ? undefined : getYear(maxValue)}
        minValue={minValue == null ? undefined : getYear(minValue)}
        size={size}
        value={getYear(value)}
        onChange={handleYearChange}
      />
      <SpinInput
        {...spinInputProps}
        className={spinInputClassName}
        disabled={spinInputDisabled || disabled}
        maxValue={maxValue}
        minValue={minValue}
        size={size}
        value={value}
        onChange={onChange}
      />
    </Element>
  )
}

export default MonthInput
